import { z } from 'zod';
import { ClinicRole, Id } from '../primitives.js';

/**
 * Контракты денег клиники. Пока — прайс-лист: без него не считается ни план
 * лечения, ни счёт.
 *
 * Цена везде в копейках целым числом. Рубли с копейками после точки в JSON
 * выглядят удобнее ровно до первого `0.1 + 0.2`, после чего расхождение
 * приходится объяснять бухгалтеру клиники.
 *
 * Документация модуля: docs/03-modules/07-finance.md
 */

/** Копейки: целое неотрицательное. Отрицательных цен в прайсе не бывает. */
export const Kopecks = z.number().int().min(0).max(1_000_000_000);

/**
 * Раздел прайса. Не справочник, а перечисление: клиника выбирает из готового
 * списка, иначе в прайсе заводится «Терапия», «терапия» и «Терапия ».
 */
export const ServiceCategory = z.enum([
  'diagnostics',
  'therapy',
  'surgery',
  'orthopedics',
  'orthodontics',
  'periodontics',
  'hygiene',
  'pediatrics',
  'implantology',
  'other',
]);
export type ServiceCategory = z.infer<typeof ServiceCategory>;

/** В чём измеряется услуга: от этого зависит смысл количества в позиции плана. */
export const ServiceUnit = z.enum([
  /** Зуб — основная единица: «лечение кариеса» умножается на число зубов. */
  'tooth',
  /** Один визит или процедура целиком. */
  'procedure',
  /** Канал, поверхность, имплант — штучное. */
  'unit',
  'hour',
]);
export type ServiceUnit = z.infer<typeof ServiceUnit>;

export const Service = z.object({
  id: Id,
  tenantId: Id,
  /** Внутренний код клиники: по нему прайс сверяют с бумажным и с 1С. */
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(300),
  category: ServiceCategory,
  unit: ServiceUnit,
  price: Kopecks,
  /**
   * Код номенклатуры Минздрава (приказ 804н). Нужен в документах для пациента
   * и в отчётности; в прайсе клиники может быть не проставлен.
   */
  nomenclatureCode: z.string().max(20).nullable(),
  /** Сколько минут занимает: подставляется в длительность визита. */
  durationMinutes: z.number().int().min(0).max(480).nullable(),
  /**
   * Услуга снята с прайса. Не удалена: она стоит в согласованных планах и в
   * счетах прошлого года, и удаление разрушило бы их.
   */
  isActive: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
});
export type Service = z.infer<typeof Service>;

export const CreateServiceRequest = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(300),
  category: ServiceCategory,
  unit: ServiceUnit,
  price: Kopecks,
  /**
   * Необязательные поля принимают и `null`, как и при правке. Для формы «поля
   * нет» и «поле оставили пустым» — одно и то же, и разница между схемами
   * означала бы, что новая услуга без кода номенклатуры не заводится вовсе, а
   * та же услуга правкой — заводится.
   */
  nomenclatureCode: z.string().max(20).nullable().optional(),
  durationMinutes: z.number().int().min(0).max(480).nullable().optional(),
});
export type CreateServiceRequest = z.infer<typeof CreateServiceRequest>;

/**
 * Правка услуги.
 *
 * Изменение цены не трогает ни один согласованный план и ни один выставленный
 * счёт: там лежит цена на момент согласования, скопированная, а не связанная
 * ссылкой. Пересчёта задним числом в системе нет.
 */
export const UpdateServiceRequest = z.object({
  name: z.string().min(1).max(300).optional(),
  category: ServiceCategory.optional(),
  unit: ServiceUnit.optional(),
  price: Kopecks.optional(),
  nomenclatureCode: z.string().max(20).nullable().optional(),
  durationMinutes: z.number().int().min(0).max(480).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateServiceRequest = z.infer<typeof UpdateServiceRequest>;

export const PriceListQuery = z.object({
  category: ServiceCategory.optional(),
  /** Поиск по коду и названию: в прайсе клиники бывает несколько сотен строк. */
  search: z.string().max(100).optional(),
  includeInactive: z.coerce.boolean().default(false),
});
export type PriceListQuery = z.infer<typeof PriceListQuery>;

export const PriceList = z.object({
  services: z.array(Service),
});
export type PriceList = z.infer<typeof PriceList>;

/**
 * Счета, оплаты, рассрочки, кассовая смена, начисления, зарплаты,
 * взаиморасчёт с лабораторией — веха 2.4.
 *
 * Статус счёта и рассрочки не приходит с клиента ни в одном запросе: он
 * выводится на сервере из суммы и уплаченного
 * (`@odonix/core.finance.statusAfterPayment`). Клиент его только показывает.
 */

// --- Счёт --------------------------------------------------------------------

export const InvoiceState = z.enum(['draft', 'issued', 'partially_paid', 'paid', 'cancelled']);
export type InvoiceState = z.infer<typeof InvoiceState>;

/** Откуда взялась позиция — для прослеживаемости, не для повторного счёта. */
export const InvoiceItemSource = z.enum(['manual', 'lab_order']);
export type InvoiceItemSource = z.infer<typeof InvoiceItemSource>;

export const InvoiceItem = z.object({
  id: Id,
  invoiceId: Id,
  /** Услуга прайса, если позиция из него — ручная позиция может быть и без неё. */
  serviceId: Id.nullable(),
  sourceType: InvoiceItemSource,
  /** `labOrderId`, когда позиция пришла из наряда автоматически на `installed`. */
  sourceId: Id.nullable(),
  /** Название скопировано на момент добавления — переименование услуги не меняет старый счёт. */
  name: z.string().min(1).max(300),
  unitPrice: Kopecks,
  quantity: z.number().positive().max(1000),
  discountPercent: z.number().min(0).max(100),
  /** Обязательна у любой скидки, кроме роли без лимита (владелец, старший администратор без ограничения). */
  discountReason: z.string().max(500).nullable(),
  isWarranty: z.boolean(),
  grossAmount: Kopecks,
  discountAmount: Kopecks,
  amount: Kopecks,
  addedBy: Id,
  /**
   * Врач, чья работа эта позиция, — не то же самое, что `addedBy` (обычно
   * стойка регистратуры). Пусто у позиций без явного автора: ручная позиция,
   * которую никто не привязал к врачу, реальнее нуля в его личной выручке.
   * У позиций из наряда лаборатории заполняется автоматически из
   * `LabOrder.doctorId` (`lab-integration.ts`) — веха 3.9.
   */
  doctorId: Id.nullable(),
  createdAt: z.iso.datetime(),
});
export type InvoiceItem = z.infer<typeof InvoiceItem>;

export const Invoice = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  state: InvoiceState,
  /** Пусто до выставления — присваивает только сервер, сквозной в пределах клиники. */
  number: z.string(),
  items: z.array(InvoiceItem),
  /**
   * Оплаты счёта, включая сторно. Вложены, как и позиции: у счёта в кэше
   * устройства не бывает состояния «есть, но без истории платежей» — приём
   * оплаты за 30 секунд читает и то, и другое одним запросом, а не тремя.
   * `z.lazy` — вперёд на `Payment`, объявленный ниже в этом же файле.
   */
  payments: z.lazy((): z.ZodType<Payment> => Payment).array(),
  grossAmount: Kopecks,
  discountAmount: Kopecks,
  totalAmount: Kopecks,
  /** Сумма активных (не сторнированных) оплат — то же, что показывает карта пациента. */
  paidAmount: Kopecks,
  createdBy: Id,
  issuedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  cancelReason: z.string().max(500).nullable(),
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
  /**
   * Визит, за который выставлен счёт: по нему сетка расписания показывает «к
   * оплате». Пусто у счёта по плану лечения и у счёта из наряда лаборатории.
   * Необязательно, а не только `nullable`: в кэше устройств лежат счета,
   * полученные до появления поля, без ключа вовсе, — а счёт устройство
   * разбирает целиком, и отсутствующий ключ не должен выбрасывать его из кэша.
   */
  appointmentId: Id.nullable().optional(),
});
export type Invoice = z.infer<typeof Invoice>;

/** Открытие счёта — черновик без позиций, к которому дальше добавляют строки. */
export const CreateInvoiceRequest = z.object({
  patientId: Id,
  /**
   * Счёт визита. Визит обязан быть этого же пациента и не отменённым — это
   * проверяет сервер; без поля счёт ни к какому визиту не относится.
   */
  appointmentId: Id.optional(),
});
export type CreateInvoiceRequest = z.infer<typeof CreateInvoiceRequest>;

export const AddInvoiceItemRequest = z.object({
  /**
   * Счёт, к которому добавляется позиция. У HTTP-маршрута он и так есть в
   * пути (`/v1/invoices/{id}/items`), но у операции из очереди устройства
   * пути нет вовсе — только это тело, — и без поля привязать позицию было
   * бы не к чему.
   */
  invoiceId: Id,
  serviceId: Id.optional(),
  name: z.string().min(1).max(300),
  unitPrice: Kopecks,
  quantity: z.number().positive().max(1000).default(1),
  discountPercent: z.number().min(0).max(100).default(0),
  /** Причина скидки — обязательна у любой скидки выше нуля, кроме владельца и старшего администратора без лимита. */
  discountReason: z.string().max(500).optional(),
  isWarranty: z.boolean().default(false),
  /** Кто выполнил работу — веха 3.9. Пусто принимается: не каждая позиция привязана к врачу. */
  doctorId: Id.nullable().optional(),
});
export type AddInvoiceItemRequest = z.infer<typeof AddInvoiceItemRequest>;

export const InvoicesQuery = z.object({
  patientId: Id.optional(),
  state: InvoiceState.optional(),
  overdue: z.coerce.boolean().optional(),
});
export type InvoicesQuery = z.infer<typeof InvoicesQuery>;

export const Invoices = z.object({ invoices: z.array(Invoice) });
export type Invoices = z.infer<typeof Invoices>;

// --- Оплата --------------------------------------------------------------------

/**
 * `wallet` — зачёт аванса с кошелька пациента: деньги уже приняты раньше
 * (депозитом, своим способом и своей сменой), поэтому такой платёж не
 * попадает ни в кассовую смену, ни в приток денег — только закрывает счёт.
 */
export const PaymentMethod = z.enum(['cash', 'card_terminal', 'bank_transfer', 'online_yookassa', 'wallet']);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

/** Чем пациент вносит аванс и чем получает возврат: живые деньги, не зачёт. */
export const WalletMoneyMethod = z.enum(['cash', 'card_terminal', 'bank_transfer']);
export type WalletMoneyMethod = z.infer<typeof WalletMoneyMethod>;

export const Payment = z.object({
  id: Id,
  tenantId: Id,
  invoiceId: Id,
  method: PaymentMethod,
  amount: Kopecks,
  receivedBy: Id,
  /** Кассовая смена, в которую попал платёж — по факту получения, не по времени применения. */
  cashShiftId: Id.nullable(),
  fiscalReceiptId: z.string().nullable(),
  /** Заполнено у самого сторно — ссылка на отменяемый платёж; у обычного платежа пусто. */
  reversalOf: Id.nullable(),
  reason: z.string().max(500).nullable(),
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type Payment = z.infer<typeof Payment>;

export const ReceivePaymentRequest = z.object({
  /** Счёт, который оплачивают. См. комментарий у `AddInvoiceItemRequest.invoiceId`. */
  invoiceId: Id,
  method: PaymentMethod,
  amount: Kopecks,
});
export type ReceivePaymentRequest = z.infer<typeof ReceivePaymentRequest>;

export const ReversePaymentRequest = z.object({
  reason: z.string().min(1).max(500),
});
export type ReversePaymentRequest = z.infer<typeof ReversePaymentRequest>;

// --- Двухшаговое сторно: заявка на возврат (веха 3.9) ---------------------------

export const PaymentReversalRequestStatus = z.enum(['pending', 'approved', 'rejected']);
export type PaymentReversalRequestStatus = z.infer<typeof PaymentReversalRequestStatus>;

export const PaymentReversalRequest = z.object({
  id: Id,
  tenantId: Id,
  paymentId: Id,
  invoiceId: Id,
  amount: Kopecks,
  reason: z.string().min(1).max(500),
  status: PaymentReversalRequestStatus,
  requestedBy: Id,
  requestedAt: z.iso.datetime(),
  decidedBy: Id.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionComment: z.string().max(500).nullable(),
  /** Заполнено только у `approved` — id проводки сторно, которую создало решение. */
  reversalPaymentId: Id.nullable(),
});
export type PaymentReversalRequest = z.infer<typeof PaymentReversalRequest>;

/** Заявка на возврат — тот же `reason`, что и у прямого сторно, только не исполняется сразу. */
export const CreatePaymentReversalRequest = z.object({
  reason: z.string().min(1).max(500),
});
export type CreatePaymentReversalRequest = z.infer<typeof CreatePaymentReversalRequest>;

export const DecidePaymentReversalRequest = z.object({
  decision: z.enum(['approve', 'reject']),
  /** Клиентский id новой проводки сторно — обязателен при `approve`, лишний при `reject`. */
  reversalPaymentId: Id.optional(),
  /** Обязателен при `reject` — почему возврат не подтверждён. */
  comment: z.string().max(500).optional(),
});
export type DecidePaymentReversalRequest = z.infer<typeof DecidePaymentReversalRequest>;

export const PaymentReversalRequestsQuery = z.object({
  status: PaymentReversalRequestStatus.optional(),
  invoiceId: Id.optional(),
});
export type PaymentReversalRequestsQuery = z.infer<typeof PaymentReversalRequestsQuery>;

// --- Рассрочка -----------------------------------------------------------------

export const InstallmentPlanState = z.enum(['active', 'completed', 'defaulted', 'cancelled']);
export type InstallmentPlanState = z.infer<typeof InstallmentPlanState>;

export const InstallmentScheduleItem = z.object({
  id: Id,
  planId: Id,
  amount: Kopecks,
  dueDate: z.iso.date(),
  paidAt: z.iso.datetime().nullable(),
  paymentId: Id.nullable(),
});
export type InstallmentScheduleItem = z.infer<typeof InstallmentScheduleItem>;

export const InstallmentPlan = z.object({
  id: Id,
  tenantId: Id,
  invoiceId: Id,
  state: InstallmentPlanState,
  schedule: z.array(InstallmentScheduleItem),
  createdBy: Id,
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type InstallmentPlan = z.infer<typeof InstallmentPlan>;

export const CreateInstallmentPlanRequest = z.object({
  /** Счёт, для которого составляют рассрочку. См. комментарий выше. */
  invoiceId: Id,
  installmentsCount: z.number().int().min(2).max(36),
  firstDueDate: z.iso.date(),
  intervalDays: z.number().int().min(1).max(90).default(30),
});
export type CreateInstallmentPlanRequest = z.infer<typeof CreateInstallmentPlanRequest>;

// --- Кассовая смена --------------------------------------------------------------

export const CashShiftState = z.enum(['open', 'closed']);
export type CashShiftState = z.infer<typeof CashShiftState>;

export const CashShift = z.object({
  id: Id,
  tenantId: Id,
  state: CashShiftState,
  openedBy: Id,
  openedAt: z.iso.datetime(),
  openingBalance: Kopecks,
  closedBy: Id.nullable(),
  closedAt: z.iso.datetime().nullable(),
  /** Ожидаемая наличность по документам — считает сервер на момент закрытия. */
  expectedCash: Kopecks.nullable(),
  actualCash: Kopecks.nullable(),
  /** Разница `actualCash − expectedCash`; может быть отрицательной — недостача. */
  discrepancy: z.number().int().nullable(),
  discrepancyComment: z.string().max(500).nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type CashShift = z.infer<typeof CashShift>;

/**
 * Открытие кассы. Остаток на начало сервер переносит с прошлой смены сам —
 * это пересчитанные при её закрытии наличные. Поле нужно только первой смене
 * клиники в программе; у следующих оно игнорируется, и приложения прежних
 * версий, которые его шлют, работают как раньше.
 */
export const OpenCashShiftRequest = z.object({
  openingBalance: Kopecks.optional(),
});
export type OpenCashShiftRequest = z.infer<typeof OpenCashShiftRequest>;

/**
 * Касса сейчас. Смену видит любой сотрудник (от неё зависит, принимается ли
 * оплата), суммы — только те, кто видит деньги клиники; остальным — `null`.
 */
export const CurrentCashShift = z.object({
  shift: CashShift.nullable(),
  /** Сколько наличных в ящике сейчас — считает сервер тем же правилом, что при закрытии. */
  inDrawer: z.number().int().nullable().default(null),
  /** Из чего сложилась сумма в ящике. */
  drawer: z
    .object({
      /** С прошлой смены (у первой смены клиники — введённое при открытии). */
      openingBalance: z.number().int(),
      /** Наличные оплаты смены за вычетом их сторно. */
      cashPayments: z.number().int(),
      walletDeposits: Kopecks,
      walletRefunds: Kopecks,
      /** Расходы наличными из ящика. */
      expenses: Kopecks.default(0),
      /** Выдано из ящика в сейф. */
      safeDeposits: Kopecks.default(0),
      total: z.number().int(),
    })
    .nullable()
    .default(null),
  /** Последняя закрытая смена: её пересчитанные наличные — остаток на начало следующей. */
  carriedFrom: z
    .object({ shiftId: Id, actualCash: Kopecks, closedAt: z.iso.datetime(), closedBy: Id })
    .nullable()
    .default(null),
  /**
   * Расходы, которые касса видит сейчас: при открытой — расходы смены (кроме
   * сейфа), при закрытой — сделанные после её закрытия и ждущие следующую
   * смену. Пусто у ролей, которым деньги клиники не видны.
   */
  expenses: z.array(z.lazy(() => Expense)).default([]),
  /** Выдачи в сейф из ящика открытой смены. */
  safeDeposits: z.array(z.lazy(() => SafeMovement)).default([]),
  /**
   * Сколько наличных можно взять из ящика прямо сейчас: при открытой кассе —
   * сумма в ящике, при закрытой — остаток прошлой смены минус наличные
   * расходы, уже сделанные после её закрытия. `null` — роль денег не видит.
   */
  availableCash: z.number().int().nullable().default(null),
  /** Остаток сейфа — тем, кто тратит из него (`finance:expense:safe`) или ведёт его; остальным `null`. */
  safeBalance: z.number().int().nullable().default(null),
});
export type CurrentCashShift = z.infer<typeof CurrentCashShift>;

export const CloseCashShiftRequest = z.object({
  actualCash: Kopecks,
  /** Обязателен, если расхождение не равно нулю — проверяется на сервере. */
  discrepancyComment: z.string().max(500).optional(),
});
export type CloseCashShiftRequest = z.infer<typeof CloseCashShiftRequest>;

/**
 * История смен за период. Границы обязательны: «все смены с начала работы»
 * — это запрос, у которого нет верхней оценки размера, а сверку наличности
 * ведут по месяцам и дням, а не по всей жизни клиники.
 */
export const CashShiftsQuery = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
});
export type CashShiftsQuery = z.infer<typeof CashShiftsQuery>;

/**
 * Смена со своими проводками — то, из чего сложилась ожидаемая наличность.
 * Без этого списка расхождение остаётся числом, которое нечем объяснить.
 */
export const CashShiftDetail = z.object({
  shift: CashShift,
  payments: z.array(Payment),
  /** Авансы и возвраты, прошедшие через эту смену; пусто у старых смен и у сервера прежней версии. */
  walletTransactions: z.array(z.lazy(() => WalletTransaction)).default([]),
  /** Расходы, которые касса видит в этой смене: наличные — из её ящика, карта и перевод — для сведения. */
  expenses: z.array(z.lazy(() => Expense)).default([]),
  /** Наличные, выданные из ящика смены в сейф. */
  safeDeposits: z.array(z.lazy(() => SafeMovement)).default([]),
});
export type CashShiftDetail = z.infer<typeof CashShiftDetail>;

// --- Кошелёк пациента: аванс, зачёт, возврат ------------------------------------

/**
 * Кошелёк — журнал, а не поле «баланс». Остаток считается суммой: депозиты и
 * зачёты/возвраты со знаком. Строки не правятся и не удаляются — как платежи.
 *
 * `deposit` — пациент внёс аванс (`method` — чем внёс, наличные попадают в
 * смену). `charge` — часть аванса зачтена в оплату счёта (`invoiceId`,
 * `paymentId` — созданный этим зачётом платёж с `method: 'wallet'`).
 * `refund` — деньги возвращены пациенту (`method` — чем вернули; наличные
 * уменьшают ожидаемую наличность смены).
 */
export const WalletTransactionKind = z.enum(['deposit', 'charge', 'refund']);
export type WalletTransactionKind = z.infer<typeof WalletTransactionKind>;

export const WalletTransaction = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  kind: WalletTransactionKind,
  /** Пусто у зачёта: деньги не двигались, только обязательство. */
  method: WalletMoneyMethod.nullable(),
  amount: Kopecks,
  invoiceId: Id.nullable(),
  paymentId: Id.nullable(),
  performedBy: Id,
  cashShiftId: Id.nullable(),
  comment: z.string().max(500).nullable(),
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type WalletTransaction = z.infer<typeof WalletTransaction>;

export const PatientWallet = z.object({
  patientId: Id,
  /** Сколько пациент может зачесть в оплату прямо сейчас. */
  balance: Kopecks,
  transactions: z.array(WalletTransaction),
});
export type PatientWallet = z.infer<typeof PatientWallet>;

/** Пополнение: клиентский `id` — ключ идемпотентности, как у платежа. */
export const WalletDepositRequest = z.object({
  id: Id,
  method: WalletMoneyMethod,
  amount: Kopecks.refine((value) => value > 0, 'Сумма аванса должна быть больше нуля'),
  comment: z.string().max(500).optional(),
  clientCreatedAt: z.iso.datetime(),
});
export type WalletDepositRequest = z.infer<typeof WalletDepositRequest>;

export const WalletRefundRequest = z.object({
  id: Id,
  method: WalletMoneyMethod,
  amount: Kopecks.refine((value) => value > 0, 'Сумма возврата должна быть больше нуля'),
  reason: z.string().min(1).max(500),
});
export type WalletRefundRequest = z.infer<typeof WalletRefundRequest>;

/** Период сводки авансов: даты `YYYY-MM-DD`, `to` включительно. */
export const WalletSummaryQuery = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
});
export type WalletSummaryQuery = z.infer<typeof WalletSummaryQuery>;

/** Авансы по клинике за период — для кабинета руководителя. */
export const WalletSummary = z.object({
  /** Остаток на всех кошельках сейчас — обязательство клиники перед пациентами. */
  balanceTotal: Kopecks,
  /** Сколько пациентов держат остаток больше нуля. */
  patientsWithBalance: z.number().int().nonnegative(),
  /** За период: внесено, зачтено в оплату, возвращено. */
  deposits: Kopecks,
  charges: Kopecks,
  refunds: Kopecks,
});
export type WalletSummary = z.infer<typeof WalletSummary>;

// --- Счёт семьи: кошельки и долги карт одной семьи -----------------------------

/**
 * Член семьи в сводке. Суммы — `null` при `restricted`: врач видит деньги
 * только своих пациентов (§2, примечание ¹), а имя родственника показывается
 * всё равно — оно часть карты, которую он и так читает.
 */
export const FamilyAccountMember = z.object({
  patientId: Id,
  fullName: z.string(),
  restricted: z.boolean(),
  /** Остаток кошелька — то же число, что `PatientWallet.balance`. */
  walletBalance: Kopecks.nullable(),
  /** Выставлено: счета в состояниях `issued`, `partially_paid`, `paid`. */
  invoicedTotal: Kopecks.nullable(),
  /** Оплачено по этим счетам за вычетом сторно. */
  paidTotal: Kopecks.nullable(),
  /** Долг: остаток по счетам `issued` и `partially_paid`. */
  outstanding: Kopecks.nullable(),
});
export type FamilyAccountMember = z.infer<typeof FamilyAccountMember>;

export const FamilyAccount = z.object({
  patientId: Id,
  /** Пусто — карта вне семьи, и в `members` она одна. */
  familyId: Id.nullable(),
  /**
   * Запрошенная карта первой, остальные по ФИО. Закрытые карты входят — долг
   * от закрытия не исчез; карты, влитые как дубли, — нет.
   */
  members: z.array(FamilyAccountMember),
  /** Суммы только по членам без `restricted`: скрытое не складывается в видимое. */
  totals: z.object({
    walletBalance: Kopecks,
    invoicedTotal: Kopecks,
    paidTotal: Kopecks,
    outstanding: Kopecks,
  }),
});
export type FamilyAccount = z.infer<typeof FamilyAccount>;

// --- Начисления и зарплата --------------------------------------------------------

/**
 * `staff_variable` — переменная часть за месяц, целиком: её начисляет
 * закрытие периода по персоналу одной строкой на сотрудника, а не по позиции
 * визита или наряду.
 */
export const AccrualKind = z.enum(['doctor_percent', 'technician_percent', 'staff_variable', 'manual']);
export type AccrualKind = z.infer<typeof AccrualKind>;

export const AccrualSource = z.enum(['invoice_item', 'lab_order', 'staff_period', 'manual']);
export type AccrualSource = z.infer<typeof AccrualSource>;

export const Accrual = z.object({
  id: Id,
  tenantId: Id,
  userId: Id,
  kind: AccrualKind,
  sourceType: AccrualSource,
  sourceId: Id,
  baseAmount: Kopecks,
  percent: z.number().min(0).max(100),
  amount: Kopecks,
  /** `YYYY-MM` — месяц начисления, по нему собирается расчёт зарплаты. */
  period: z.string().regex(/^\d{4}-\d{2}$/),
  createdAt: z.iso.datetime(),
  /**
   * Разбор переменной части — присутствует только у `kind: 'staff_variable'`,
   * заведённого после того, как разбор стали сохранять: у более старых строк
   * и у остальных `kind` ключей нет вовсе, а не `null` — молчание не намекает,
   * что скрывать было что-то конкретное (тот же приём, что у `labCost`
   * в наряде лаборатории).
   */
  fromPercentKopecks: z.number().int().optional(),
  fromKpiKopecks: z.number().int().optional(),
  adjustmentsKopecks: z.number().int().optional(),
  /** Основание — заполнено только у `kind: 'manual'`; у остальных `null`. */
  comment: z.string().max(500).nullable(),
});
export type Accrual = z.infer<typeof Accrual>;

export const AccrualsQuery = z.object({
  userId: Id.optional(),
  period: z.string().regex(/^\d{4}-\d{2}$/).optional(),
});
export type AccrualsQuery = z.infer<typeof AccrualsQuery>;

/**
 * Ручное начисление — оплата, которую не рождает ни один сценарий системы:
 * замещение приёма, разовый выход без визитов, разовая доплата вне схемы
 * мотивации. В отличие от премии (`CreatePayrollAdjustmentRequest`), не ждёт
 * закрытия месяца и не входит в переменную часть — начисление видно сразу,
 * тем же путём, что и оплата визита или закрытый наряд лаборатории.
 */
export const CreateManualAccrualRequest = z.object({
  userId: Id,
  /** `YYYY-MM` — период, в расчёт зарплаты за который попадёт начисление. */
  period: z.string().regex(/^\d{4}-\d{2}$/),
  amountKopecks: Kopecks,
  reason: z.string().trim().min(3).max(500),
});
export type CreateManualAccrualRequest = z.infer<typeof CreateManualAccrualRequest>;

export const Accruals = z.object({ accruals: z.array(Accrual) });
export type Accruals = z.infer<typeof Accruals>;

/**
 * Расчёт за период — сумма готовых начислений, а не второй расчёт: Finance
 * берёт цифры из `Accrual`, которые сам же и создал на `closed` наряда и на
 * оплате визита. Переменная часть сверх процента (премии, KPI) — модуль
 * Staff, отдельные строки `Accrual` того же периода, здесь не различаются.
 */
export const SalaryPeriodQuery = z.object({
  /** Пусто — отчёт по всем, у кого есть начисления за период (только владелец и бухгалтер). */
  userId: Id.optional(),
});
export type SalaryPeriodQuery = z.infer<typeof SalaryPeriodQuery>;

export const SalaryPeriodView = z.object({
  tenantId: Id,
  userId: Id,
  period: z.string().regex(/^\d{4}-\d{2}$/),
  totalAmount: Kopecks,
  accruals: z.array(Accrual),
  closedAt: z.iso.datetime().nullable(),
  closedBy: Id.nullable(),
});
export type SalaryPeriodView = z.infer<typeof SalaryPeriodView>;

/** Ответ без `userId` в запросе: по одному расчёту на каждого, у кого есть начисления за период. */
export const SalaryPeriodReport = z.object({ period: z.string().regex(/^\d{4}-\d{2}$/), entries: z.array(SalaryPeriodView) });
export type SalaryPeriodReport = z.infer<typeof SalaryPeriodReport>;

// --- Взаиморасчёт с лабораторией --------------------------------------------------

export const LabSettlementState = z.enum(['open', 'closed']);
export type LabSettlementState = z.infer<typeof LabSettlementState>;

export const LabSettlement = z.object({
  clinicTenantId: Id,
  labTenantId: Id,
  /**
   * Карточка лаборатории, заведённой клиникой вручную. Пусто у партнёрской
   * строки — там лабораторию называет `labTenantId`. Вместе они и есть ключ
   * лаборатории на экране: партнёр — по арендатору, карточка — по себе.
   */
  laboratoryId: Id.nullable().default(null),
  labName: z.string(),
  period: z.string().regex(/^\d{4}-\d{2}$/),
  state: LabSettlementState,
  /**
   * Сколько нарядов закрыто в периоде и на какую сумму для клиники — то, что
   * клиника должна лаборатории. Себестоимости здесь нет: она внутреннее число
   * лаборатории, а строку читают обе стороны.
   */
  ordersClosed: z.number().int().nonnegative(),
  totalAmount: Kopecks,
  closedAt: z.iso.datetime().nullable(),
  closedBy: Id.nullable(),
});
export type LabSettlement = z.infer<typeof LabSettlement>;

export const CloseLabSettlementRequest = z.object({
  labTenantId: Id,
  laboratoryId: Id.nullable().optional(),
  period: z.string().regex(/^\d{4}-\d{2}$/),
});
export type CloseLabSettlementRequest = z.infer<typeof CloseLabSettlementRequest>;

export const LabSettlementsQuery = z.object({
  labTenantId: Id.optional(),
  laboratoryId: Id.optional(),
  period: z.string().regex(/^\d{4}-\d{2}$/).optional(),
});
export type LabSettlementsQuery = z.infer<typeof LabSettlementsQuery>;

/**
 * Наряд в составе взаиморасчёта: что закрыто в периоде, кому и за сколько.
 *
 * Имя пациента здесь, а не подстановкой из кэша устройства: взаиморасчёт
 * читают за прошлые месяцы, а кэш держит только рабочее окно.
 */
/**
 * Позиция прейскуранта в наряде взаиморасчёта — то, из чего сложена сумма
 * наряда. Нужна печатной форме: реестр «наряд — сумма» без разбивки
 * лаборатория сверить не может, у неё в учёте стоят работы, а не наряды.
 */
export const LabSettlementOrderItem = z.object({
  id: Id,
  name: z.string(),
  unitPrice: Kopecks,
  quantity: z.number().int().positive(),
  amount: Kopecks,
});
export type LabSettlementOrderItem = z.infer<typeof LabSettlementOrderItem>;

export const LabSettlementOrder = z.object({
  orderId: Id,
  number: z.string(),
  /** Пусто у наряда от бумажной клиники — тогда имя пациента пришло текстом с самого наряда. */
  patientId: Id.nullable(),
  patientName: z.string(),
  doctorId: Id.nullable(),
  restorationType: z.string(),
  materialCode: z.string(),
  status: z.string(),
  /** Когда наряд перешёл в `closed` — по этому месяцу он и попал в период. */
  closedAt: z.iso.datetime(),
  clinicPrice: Kopecks,
  /** Себестоимость — только в ответе лаборатории; клинике приходит `null`, как и в наряде. */
  labCost: Kopecks.nullable(),
  /** Разбивка суммы по прейскуранту. Пусто у наряда, заведённого одним числом. */
  items: z.array(LabSettlementOrderItem).default([]),
  /** Зубы конструкции — для печатной формы; пусто у наряда без привязки к зубам. */
  units: z.array(z.object({ fdi: z.number().int(), role: z.string() })).default([]),
});
export type LabSettlementOrder = z.infer<typeof LabSettlementOrder>;

export const LabSettlementOrdersQuery = z.object({
  /** Клиника строки. Клиника не передаёт (это она сама); лаборатория берёт из строки. */
  clinicTenantId: Id.optional(),
  labTenantId: Id,
  laboratoryId: Id.optional(),
  period: z.string().regex(/^\d{4}-\d{2}$/),
});
export type LabSettlementOrdersQuery = z.infer<typeof LabSettlementOrdersQuery>;

export const LabSettlementOrders = z.object({ orders: z.array(LabSettlementOrder) });
export type LabSettlementOrders = z.infer<typeof LabSettlementOrders>;

// --- Предел скидки по роли --------------------------------------------------------

/** Предел не задан (`null`) — как у владельца: скидка любого размера без причины. */
export const DiscountRule = z.object({
  tenantId: Id,
  role: ClinicRole,
  maxDiscountPercent: z.number().min(0).max(100).nullable(),
  updatedBy: Id,
  updatedAt: z.iso.datetime(),
});
export type DiscountRule = z.infer<typeof DiscountRule>;

export const UpdateDiscountRuleRequest = z.object({
  role: ClinicRole,
  maxDiscountPercent: z.number().min(0).max(100).nullable(),
});
export type UpdateDiscountRuleRequest = z.infer<typeof UpdateDiscountRuleRequest>;

export const DiscountRules = z.object({ rules: z.array(DiscountRule) });
export type DiscountRules = z.infer<typeof DiscountRules>;

// --- Прочие расходы (веха 3.9) -----------------------------------------------------

export const ManualExpenseCategory = z.enum(['rent', 'utilities', 'other']);
export type ManualExpenseCategory = z.infer<typeof ManualExpenseCategory>;

export const ManualExpense = z.object({
  id: Id,
  tenantId: Id,
  category: ManualExpenseCategory,
  amountKopecks: Kopecks,
  /** `YYYY-MM` — месяц, к которому отнесён расход, не дата ввода. */
  period: z.string().regex(/^\d{4}-\d{2}$/),
  comment: z.string().max(500).nullable(),
  createdBy: Id,
  createdAt: z.iso.datetime(),
  deactivatedBy: Id.nullable(),
  deactivatedAt: z.iso.datetime().nullable(),
});
export type ManualExpense = z.infer<typeof ManualExpense>;

export const CreateManualExpenseRequest = z.object({
  category: ManualExpenseCategory,
  amountKopecks: Kopecks,
  period: z.string().regex(/^\d{4}-\d{2}$/),
  comment: z.string().max(500).nullable().optional(),
});
export type CreateManualExpenseRequest = z.infer<typeof CreateManualExpenseRequest>;

export const ManualExpensesQuery = z.object({
  period: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  category: ManualExpenseCategory.optional(),
});
export type ManualExpensesQuery = z.infer<typeof ManualExpensesQuery>;

export const ManualExpenses = z.object({ expenses: z.array(ManualExpense) });
export type ManualExpenses = z.infer<typeof ManualExpenses>;

// --- Расходы и сейф ----------------------------------------------------------------

/**
 * Чем оплачен расход. Наличные берутся из ящика открытой смены (или, при
 * закрытой кассе, из того, что осталось с прошлой, — и ложатся на следующую);
 * карта и перевод ящика не касаются; сейф — деньги, выданные в него из кассы.
 */
export const ExpenseMethod = z.enum(['cash', 'card_terminal', 'bank_transfer', 'safe']);
export type ExpenseMethod = z.infer<typeof ExpenseMethod>;

export const ExpenseCategory = z.object({
  id: Id,
  tenantId: Id,
  name: z.string().min(1).max(80),
  /** Снятый вид больше не предлагается, но остаётся у прошлых расходов. */
  active: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ExpenseCategory = z.infer<typeof ExpenseCategory>;

export const ExpenseCategories = z.object({ categories: z.array(ExpenseCategory) });
export type ExpenseCategories = z.infer<typeof ExpenseCategories>;

export const CreateExpenseCategoryRequest = z.object({
  id: Id,
  name: z.string().trim().min(1).max(80),
});
export type CreateExpenseCategoryRequest = z.infer<typeof CreateExpenseCategoryRequest>;

export const UpdateExpenseCategoryRequest = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    active: z.boolean().optional(),
  })
  .refine((value) => value.name !== undefined || value.active !== undefined, { message: 'Нечего менять' });
export type UpdateExpenseCategoryRequest = z.infer<typeof UpdateExpenseCategoryRequest>;

export const Expense = z.object({
  id: Id,
  tenantId: Id,
  categoryId: Id,
  /** Имя вида на сейчас — переименованный вид переименовывается и в прошлых расходах. */
  categoryName: z.string(),
  method: ExpenseMethod,
  amount: Kopecks,
  comment: z.string().max(500).nullable(),
  performedBy: Id,
  /** Смена, в которой расход виден кассе; `null` — сделан при закрытой кассе и ждёт следующую, или из сейфа. */
  cashShiftId: Id.nullable(),
  spentAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  cancelledAt: z.iso.datetime().nullable(),
  cancelledBy: Id.nullable(),
  cancelReason: z.string().nullable(),
  /** Перенесён из «Прочих расходов» — дата у него месяц, к которому расход отнесён. */
  legacy: z.boolean().default(false),
});
export type Expense = z.infer<typeof Expense>;

export const CreateExpenseRequest = z.object({
  /** Клиентский UUIDv7 — ключ идемпотентности: повтор вернёт тот же расход, а не второй. */
  id: Id,
  categoryId: Id,
  method: ExpenseMethod,
  amount: Kopecks.min(1),
  comment: z.string().trim().max(500).optional(),
});
export type CreateExpenseRequest = z.infer<typeof CreateExpenseRequest>;

export const CancelExpenseRequest = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type CancelExpenseRequest = z.infer<typeof CancelExpenseRequest>;

/** Журнал расходов за период; границы включительно по дню и обязательны, как у смен. */
export const ExpensesQuery = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  categoryId: Id.optional(),
  method: ExpenseMethod.optional(),
  performedBy: Id.optional(),
});
export type ExpensesQuery = z.infer<typeof ExpensesQuery>;

export const ExpensesReport = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  /** Новые сверху; отменённые — тоже, со своей отметкой: журнал не теряет строк. */
  expenses: z.array(Expense),
  /** Итоги — только по действующим расходам. */
  total: Kopecks,
  byCategory: z.array(z.object({ categoryId: Id, name: z.string(), amount: Kopecks, count: z.number().int().nonnegative() })),
  byMethod: z.array(z.object({ method: ExpenseMethod, amount: Kopecks, count: z.number().int().nonnegative() })),
});
export type ExpensesReport = z.infer<typeof ExpensesReport>;

/**
 * Движение сейфа. `from_drawer` — наличные из открытой кассы в сейф: деньги
 * клиники не ушли, а переложены, поэтому это не расход. `withdrawal` — изъятие
 * из сейфа владельцем, всегда с комментарием.
 */
export const SafeMovementKind = z.enum(['from_drawer', 'withdrawal']);
export type SafeMovementKind = z.infer<typeof SafeMovementKind>;

export const SafeMovement = z.object({
  id: Id,
  tenantId: Id,
  kind: SafeMovementKind,
  amount: Kopecks,
  cashShiftId: Id.nullable(),
  comment: z.string().max(500).nullable(),
  performedBy: Id,
  createdAt: z.iso.datetime(),
});
export type SafeMovement = z.infer<typeof SafeMovement>;

export const SafeDepositRequest = z.object({
  id: Id,
  amount: Kopecks.min(1),
  comment: z.string().trim().max(500).optional(),
});
export type SafeDepositRequest = z.infer<typeof SafeDepositRequest>;

export const SafeWithdrawalRequest = z.object({
  id: Id,
  amount: Kopecks.min(1),
  comment: z.string().trim().min(1).max(500),
});
export type SafeWithdrawalRequest = z.infer<typeof SafeWithdrawalRequest>;

export const SafeQuery = z.object({ from: z.iso.date(), to: z.iso.date() });
export type SafeQuery = z.infer<typeof SafeQuery>;

/** Строка истории сейфа: выдача из кассы, изъятие или расход из сейфа — одной лентой. */
export const SafeEntry = z.object({
  id: Id,
  kind: z.enum(['from_drawer', 'withdrawal', 'expense']),
  amount: Kopecks,
  at: z.iso.datetime(),
  performedBy: Id,
  comment: z.string().nullable(),
  cashShiftId: Id.nullable(),
  /** Только у расхода. */
  categoryName: z.string().nullable(),
  /** Только у расхода: отменённый расход сейф не уменьшает. */
  cancelledAt: z.iso.datetime().nullable(),
});
export type SafeEntry = z.infer<typeof SafeEntry>;

export const SafeView = z.object({
  /** Сколько в сейфе сейчас — за всё время, не за период. */
  balance: z.number().int(),
  /** Остаток на начало периода — до первой его строки. */
  openingBalance: z.number().int().default(0),
  /** Остаток на конец периода: начало + выдачи − изъятия − расходы периода. */
  closingBalance: z.number().int().default(0),
  from: z.iso.date(),
  to: z.iso.date(),
  /** Новые сверху. */
  entries: z.array(SafeEntry),
  /** Итоги периода: выдано из кассы, изъято, потрачено из сейфа. */
  deposited: Kopecks,
  withdrawn: Kopecks,
  spent: Kopecks,
  /** Сколько сейчас в открытой кассе — предел «Изъять из кассы в сейф»; `null` — касса закрыта. */
  inDrawer: z.number().int().nullable(),
});
export type SafeView = z.infer<typeof SafeView>;

// --- Отчёты ------------------------------------------------------------------------

export const DailyCashReportQuery = z.object({ date: z.iso.date() });
export type DailyCashReportQuery = z.infer<typeof DailyCashReportQuery>;

export const DailyCashReport = z.object({
  date: z.iso.date(),
  byMethod: z.array(z.object({ method: PaymentMethod, amount: Kopecks, count: z.number().int().nonnegative() })),
  total: Kopecks,
  shifts: z.array(CashShift),
  /** Проводки дня по отдельности, новые сверху — касса без них сухая сводка. */
  payments: z.array(Payment),
});
export type DailyCashReport = z.infer<typeof DailyCashReport>;

export const DebtEntry = z.object({
  patientId: Id,
  /**
   * ФИО и телефон пациента — здесь, а не подстановкой из кэша устройства:
   * долги считаются по всей клинике, а кэш держит только рабочее окно, и у
   * половины строк имя оказалось бы «пациент не загружен».
   */
  patientName: z.string(),
  patientPhone: z.string().nullable(),
  invoiceId: Id,
  invoiceNumber: z.string(),
  totalAmount: Kopecks,
  paidAmount: Kopecks,
  outstanding: Kopecks,
  issuedAt: z.iso.datetime().nullable(),
  /**
   * Врачи долга: чья работа в позициях счёта и врач визита, за который счёт
   * выставлен; если в счёте врач не указан — врачи, у которых пациент был или
   * записан на приём (тогда `doctorsFromVisits`). По ним экран «Долги» отбирает
   * долги врача. Имена — в том же порядке, что идентификаторы. Необязательно —
   * сервер прежней версии их не присылает.
   */
  doctorIds: z.array(Id).optional(),
  doctorNames: z.array(z.string()).optional(),
  doctorsFromVisits: z.boolean().optional(),
});
export type DebtEntry = z.infer<typeof DebtEntry>;

export const DebtsReport = z.object({ debts: z.array(DebtEntry) });
export type DebtsReport = z.infer<typeof DebtsReport>;

/** Реестр счетов за период — отчёт «Счета». Период по дате создания, `YYYY-MM-DD` включительно. */
export const InvoiceRegistryQuery = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
});
export type InvoiceRegistryQuery = z.infer<typeof InvoiceRegistryQuery>;

export const InvoiceRegistryRow = z.object({
  invoiceId: Id,
  /** Пусто у черновика. */
  number: z.string(),
  status: InvoiceState,
  createdAt: z.iso.datetime(),
  issuedAt: z.iso.datetime().nullable(),
  patientId: Id,
  /** Пусто, если карта пациента недоступна этой роли. */
  patientName: z.string(),
  doctorNames: z.array(z.string()),
  totalAmount: Kopecks,
  paidAmount: Kopecks,
  outstanding: Kopecks,
  methods: z.array(z.object({ method: PaymentMethod, amount: Kopecks })),
});
export type InvoiceRegistryRow = z.infer<typeof InvoiceRegistryRow>;

export const InvoiceRegistry = z.object({
  rows: z.array(InvoiceRegistryRow),
  totals: z.object({
    count: z.number().int().nonnegative(),
    totalAmount: Kopecks,
    paidAmount: Kopecks,
    outstanding: Kopecks,
  }),
});
export type InvoiceRegistry = z.infer<typeof InvoiceRegistry>;
